/**
 * Insurance and Coverage DTOs
 * Defines data contracts for patient insurance coverage
 */

import { AuditFields } from '../common/base.dto';

/**
 * Insurance/Coverage Types
 */
export enum CoverageType {
  PRIVATE = 'Private',
  MEDICAID = 'Medicaid',
  MEDICARE = 'Medicare',
  OTHER = 'Other'
}

/**
 * COB (Coordination of Benefits) Order
 */
export enum COBOrder {
  PRIMARY = 'Primary',
  SECONDARY = 'Secondary',
  TERTIARY = 'Tertiary'
}

/**
 * Coverage Status - derived by the server from the dates, in the agency timezone (REQ-INS-04).
 * TERMINATED is the legacy stored value for EXPIRED.
 */
export enum CoverageStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  PENDING = 'Pending',
  EXPIRED = 'Expired',
  TERMINATED = 'Terminated'
}

/** Policyholder relationship (CMS-1500 box 6). Medicaid: the insured is the patient. */
export type SubscriberRelationship = 'SELF' | 'CHILD' | 'SPOUSE' | 'OTHER';

/** Medicaid program codes (medicaidType). STAR Kids and STAR+PLUS require an MCO. */
export type MedicaidProgramCode = 'FFS' | 'STAR' | 'STAR_KIDS' | 'STAR_PLUS' | 'STAR_HEALTH' | 'MCO';

export interface PostalAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

/** Manual eligibility verification entry (REQ-INS-10). Append-only. */
export interface VerificationLogEntry {
  id: string;
  verifiedDate: string;
  method: 'PORTAL' | 'PHONE' | 'FAX';
  contactName?: string | null;
  referenceNumber?: string | null;
  pdnCovered: 'Y' | 'N';
  hourOrVisitLimit?: string | null;
  priorAuthRequired: 'Y' | 'N';
  deductible?: string | null;
  copay?: string | null;
  eligibilityStart?: string | null;
  eligibilityEnd?: string | null;
  note?: string | null;
  createdBy: string;
  createdAt: string;
}

export interface LogVerificationRequest {
  verifiedDate: string;
  method: VerificationLogEntry['method'];
  contactName?: string;
  referenceNumber?: string;
  pdnCovered: 'Y' | 'N';
  hourOrVisitLimit?: string;
  priorAuthRequired: 'Y' | 'N';
  deductible?: string;
  copay?: string;
  eligibilityStart?: string;
  eligibilityEnd?: string;
  note?: string;
}

/** One payer in effect today, for badges and the header line (no IDs). */
export interface ActivePayer {
  cobOrder: string;
  coverageType: string;
  payerName: string;
  program: string | null;
}

/**
 * Patient insurance summary (REQ-INS-05), recomputed by the server on every coverage change and
 * nightly. The patient list carries this without IDs.
 */
export interface InsuranceSummary {
  primaryPayerType: string | null;
  primaryPayerName: string | null;
  evvRequired: boolean;
  program: string | null;
  mco: string | null;
  hasActivePrimary: boolean;
  activePayers: ActivePayer[];
  activeCount: number;
  count: number;
  asOf?: string;
  nextChangeDate?: string | null;
  computedAt?: string;
}

/** MCO choice from system config (systemConfig.payers.medicaidMcos). */
export interface MedicaidMcoOption {
  code: string;
  label: string;
  legacy?: boolean;
}

export interface InsuranceSettings {
  medicaidMcos: MedicaidMcoOption[];
  medicaidVerifyDays: number;
}

/**
 * Medicare Type
 */
export enum MedicareType {
  PART_A = 'Part A',
  PART_B = 'Part B',
  PART_C = 'Part C (Advantage)',
  PART_D = 'Part D'
}

/**
 * Verification Status
 */
export enum VerificationStatus {
  VERIFIED = 'Verified',
  PENDING = 'Pending',
  FAILED = 'Failed',
  NOT_VERIFIED = 'Not Verified'
}

/**
 * Insurance Coverage data structure
 */
export interface InsuranceCoverage extends Partial<AuditFields> {
  id?: string | number;
  coverageId?: string;
  patientId?: string;
  tenantId?: string;
  
  // Coverage basics
  coverageType: CoverageType;
  payerName: string;
  memberId: string;
  
  // Dates
  effectiveDate: string;
  terminationDate?: string;
  
  // Status and order
  status: CoverageStatus;
  cobOrder: COBOrder;
  isPrimary?: boolean;
  
  // Member information
  memberName?: string;
  memberDOB?: string;
  relationshipToSubscriber?: SubscriberRelationship | string;

  // Subscriber (policyholder) information - CMS-1500 boxes 4, 7, 11
  subscriberName?: string;
  subscriberDOB?: string;
  subscriberSex?: 'M' | 'F' | 'U';
  subscriberAddress?: PostalAddress;
  employerName?: string;
  groupNumber?: string;
  planType?: string;

  // Contact information
  claimsPhone?: string;
  priorAuthPhone?: string;

  // Private Insurance specific
  requiresPriorAuth?: 'Yes' | 'No' | 'Unknown';

  // Medicare specific
  medicareMBI?: string;
  medicareClaimNumber?: string;
  
  // Medicare - Part A
  partAEffectiveDate?: string;
  partATerminationDate?: string;
  
  // Medicare - Part B
  partBEffectiveDate?: string;
  partBTerminationDate?: string;
  
  // Medicare Advantage
  hasMedicareAdvantage?: boolean;
  maPlanName?: string;
  maPlanID?: string;
  maPlanType?: string;
  maCustomerServicePhone?: string;
  maPriorAuthPhone?: string;
  
  // Part D
  hasPartD?: boolean;
  partDPlanName?: string;
  partDPlanID?: string;
  partDEffectiveDate?: string;
  
  // Medigap/Supplemental
  hasMedigap?: boolean;
  supplementalPlanName?: string;
  supplementalPolicyNumber?: string;
  supplementalPhone?: string;
  
  // Medicaid specific
  medicaidProgram?: string;
  medicaidMCO?: string;
  medicaidMCOOther?: string;
  medicaidType?: MedicaidProgramCode | string;
  medicaidIdNumber?: string;
  /** Stored in the pre-Phase 7 TX-########## format; must be re-entered as 9 digits */
  medicaidIdLegacy?: boolean;
  mcoMemberId?: string;
  mcoCustomerServicePhone?: string;
  mcoPriorAuthPhone?: string;
  stateOfEligibility?: string;
  countyOfEligibility?: string;
  eligibilityCategory?: string;
  caseManagerName?: string;
  caseManagerPhone?: string;
  
  // Coverage details
  inNetwork?: boolean;
  referralRequired?: boolean;
  paRequired?: boolean;
  
  // PCP information
  pcpName?: string;
  pcpNPI?: string;
  
  // Verification (REQ-INS-10/11). The legacy free-form fields are display-only.
  verificationLog?: VerificationLogEntry[];
  lastVerifiedAt?: string;
  /** Medicaid not verified within the configured window (default 30 days) */
  verificationDue?: boolean;
  verificationStatus?: VerificationStatus;
  lastVerifiedDate?: string;
  verificationNotes?: string;
  
  // Additional info
  notes?: string;
}

/**
 * Request for creating insurance coverage
 */
export interface CreateInsuranceCoverageRequest {
  coverageType: CoverageType;
  payerName: string;
  memberId: string;
  effectiveDate: string;
  terminationDate?: string;
  cobOrder: COBOrder;

  // Member information
  memberName?: string;
  memberDOB?: string;
  relationshipToSubscriber?: SubscriberRelationship | string;

  // Subscriber (policyholder) information
  subscriberName?: string;
  subscriberDOB?: string;
  subscriberSex?: 'M' | 'F' | 'U';
  subscriberAddress?: PostalAddress;
  employerName?: string;
  groupNumber?: string;
  planType?: string;

  // Contact information
  claimsPhone?: string;
  priorAuthPhone?: string;

  // Private Insurance specific
  requiresPriorAuth?: 'Yes' | 'No' | 'Unknown';

  // Medicare specific
  medicareMBI?: string;
  medicareClaimNumber?: string;
  
  // Medicare - Part A
  partAEffectiveDate?: string;
  partATerminationDate?: string;
  
  // Medicare - Part B
  partBEffectiveDate?: string;
  partBTerminationDate?: string;
  
  // Medicare Advantage
  hasMedicareAdvantage?: boolean;
  maPlanName?: string;
  maPlanID?: string;
  maPlanType?: string;
  maCustomerServicePhone?: string;
  maPriorAuthPhone?: string;
  
  // Part D
  hasPartD?: boolean;
  partDPlanName?: string;
  partDPlanID?: string;
  partDEffectiveDate?: string;
  
  // Medigap/Supplemental
  hasMedigap?: boolean;
  supplementalPlanName?: string;
  supplementalPolicyNumber?: string;
  supplementalPhone?: string;
  
  // Medicaid specific
  medicaidProgram?: string;
  medicaidMCO?: string;
  medicaidMCOOther?: string;
  medicaidType?: MedicaidProgramCode | string;
  medicaidIdNumber?: string;
  mcoMemberId?: string;
  mcoCustomerServicePhone?: string;
  mcoPriorAuthPhone?: string;
  stateOfEligibility?: string;
  countyOfEligibility?: string;
  eligibilityCategory?: string;
  caseManagerName?: string;
  caseManagerPhone?: string;

  // Coverage details
  inNetwork?: boolean;
  referralRequired?: boolean;
  paRequired?: boolean;

  // PCP information
  pcpName?: string;
  pcpNPI?: string;

  // Additional info
  notes?: string;
}

/**
 * Response for creating insurance coverage
 */
export interface CreateInsuranceCoverageResponse {
  success: boolean;
  coverageId: string;
  coverage: InsuranceCoverage;
  insuranceSummary?: InsuranceSummary | null;
}

/**
 * Response for listing insurance coverages
 */
export interface ListInsuranceCoveragesResponse {
  coverages: InsuranceCoverage[];
  count: number;
  insuranceSummary?: InsuranceSummary | null;
  settings?: InsuranceSettings;
}

/**
 * Insurance history entry
 */
export interface InsuranceHistoryEntry extends Partial<AuditFields> {
  id: string;
  coverageId: string;
  action: 'ADDED' | 'EDITED' | 'TERMINATED' | 'VERIFIED';
  timestamp: string;
  performedBy: string;
  details?: string;
}
